import { trackEvent } from '@intercom/messenger-js-sdk'
// src/lib/gtm.ts
import store from 'reduxStore'
import { selectAuthUser } from 'reduxStore/auth'
import { selectCurrentEditPage } from 'reduxStore/edit'
import { selectSubscription } from 'reduxStore/projects/selectors'

const DEV_MODE = import.meta.env.VITE_DEV_MODE
const isProduction = DEV_MODE === 'production'

/**
 * Single source of truth for the user's subscription tier so every analytics
 * event reports the same shape. Returns 'paid' | 'trial' | 'free'. Returns
 * null only when there's no subscription data loaded yet.
 *
 * `subscriptionId` lives on the redux ProjectsState alongside `subscription`,
 * so callers from a component pass it in. From the dataLayer helper below we
 * read it from redux directly.
 */
export function getSubscriptionTier(
  subscription: { isTrial?: boolean; planId?: string | null } | null | undefined,
  subscriptionId?: string | null,
): 'paid' | 'trial' | 'free' | null {
  if (!subscription) return null
  if (subscription.isTrial) return 'trial'
  if (subscriptionId) return 'paid'
  // Fallback when subscriptionId isn't passed: treat any non-zero/non-null
  // planId as paid. Matches the existing isPro check in EditMode.
  if (subscription.planId && subscription.planId !== '0') return 'paid'
  return 'free'
}

export function pushToDataLayer(eventData: Record<string, any>) {
  // Only push to dataLayer in production
  if (!isProduction) {
    return
  }

  try {
    console.log('Preparing to push to GTM dataLayer with eventData:', eventData)
    const state = store.getState()
    const authUser = selectAuthUser(state)
    const editPage = selectCurrentEditPage(state)
    const subscription = selectSubscription(state)

    const projectId = editPage?.id
    const pageId = editPage?.pageId
    const userId = authUser?.uid

    const lastLogin = Number(authUser?.metadata?.lastLoginAt || authUser?.lastLoginAt)
    const daysSinceLastLogin = lastLogin
      ? Math.floor((Date.now() - new Date(lastLogin).getTime()) / (1000 * 60 * 60 * 24))
      : null
    console.log('Pushing to GTM dataLayer:', {
      ...eventData,
      current_user_id: userId,
      project_id: projectId,
      page_id: pageId,
      days_since_last_login: daysSinceLastLogin,
    })

    const subscriptionId = (state as { projects?: { subscriptionId?: string | null } })?.projects?.subscriptionId ?? null
    const subscriptionStatus = getSubscriptionTier(subscription, subscriptionId)

    window.dataLayer.push({
      ...eventData,
      current_user_id: eventData.current_user_id || userId,
      project_id: eventData.project_id || projectId,
      page_id: pageId,
      days_since_last_login: daysSinceLastLogin,
      subscription_status: eventData.subscription_status || subscriptionStatus,
    })
  } catch (error) {
    console.error('GTM dataLayer push failed', error)
  }
}

export function pushUserIdentifiedEvent() {
  const state = store.getState()
  const authUser = selectAuthUser(state)
  const subscription = selectSubscription(state)
  console.log(subscription, 'user subscription')

  if (!isProduction || !authUser?.uid) return

  try {
    const subStatus = subscription?.isTrial ? 'trial' : subscription?.subscriptionId ? 'paid' : 'free'

    window.dataLayer.push({
      event: 'user_identified',
      current_user_id: authUser.uid,
      user_properties: {
        email: authUser.email,
        subscription_status: subStatus,
        plan_id: subscription?.planId || '0',
        trade: subscription?.trade || 'unknown',
        full_name: subscription?.fullName || '',
        phone: subscription?.phone || '',
        trial_end: subscription?.trialEnd || '',
        account_created_at: authUser.metadata?.creationTime || '',
      },
    })
  } catch (error) {
    console.error('GTM dataLayer push failed', error)
  }
}

export function pushIntercomEvent(eventName: string, metadata: Record<string, any> = {}) {
  try {
    const state = store.getState()
    const authUser = selectAuthUser(state)
    const editPage = selectCurrentEditPage(state)
    const subscription = selectSubscription(state)
    const subscriptionId = (state as { projects?: { subscriptionId?: string | null } })?.projects?.subscriptionId ?? null
    const subscriptionStatus = getSubscriptionTier(subscription, subscriptionId)
    const projectId = editPage?.id
    const pageId = editPage?.pageId
    const userId = authUser?.uid
    console.log('test')

    if (window.Intercom) {
      trackEvent(eventName, {
        ...metadata,
        current_user_id: metadata.current_user_id || userId,
        project_id: metadata.project_id || projectId,
        page_id: pageId,
        subscription_status: metadata.subscription_status || subscriptionStatus,
      })
    }
  } catch (error) {
    console.error('Error pushing Intercom event:', error)
  }
}

export function trackFreeUploadLimitReached(data: {
  limitType: 'file_size' | 'page_count'
  fileSizeBytes: number
  pageCount?: number
}) {
  const metadata = {
    event_category: 'Free Subscription',
    event_action: 'Upload Limit Reached',
    limit_type: data.limitType,
    file_size_bytes: data.fileSizeBytes,
    page_count: data.pageCount,
  }
  pushToDataLayer({ event: 'free_upload_limit_reached', ...metadata })
  pushIntercomEvent('free_upload_limit_reached', metadata)
}

/** How a tool was picked: a toolbar click, a keyboard shortcut, the assistant, or the walkthrough's tool picker. */
export type ToolSource = 'toolbar' | 'shortcut' | 'assistant' | 'walkthrough'

export function trackToolSelected(data: { tool: string; source: ToolSource }) {
  pushToDataLayer({
    event: 'tool_selected',
    tool: data.tool,
    source: data.source,
  })
}

export function trackShareLinkCopied(data: { projectId: string; source: 'share_popover' | 'invite_modal' }) {
  pushToDataLayer({
    event: 'share_link_copied',
    project_id: data.projectId,
    source: data.source,
  })
}

export function trackTagAdded(data: { tag: string; shapeType: string; totalTags: number }) {
  pushToDataLayer({
    event: 'tag_added',
    tag: data.tag,
    shape_type: data.shapeType,
    total_tags: data.totalTags,
  })
}

export function trackScopeAttached(data: {
  calculatorType: string
  shapeType: string
  productId: string
  isFromSuggestion: boolean
  scheduleId?: string
  confidence?: string
  specCount?: number
}) {
  pushToDataLayer({
    event: 'scope_attached',
    scope_type: data.calculatorType,
    shape_type: data.shapeType,
    product_id: data.productId,
    is_from_suggestion: data.isFromSuggestion,
    schedule_id: data.scheduleId || null,
    confidence: data.confidence || null,
    spec_count: data.specCount || 0,
  })

  // pushIntercomEvent('scope_attached', {
  //   scope_type: data.calculatorType,
  //   shape_type: data.shapeType,
  //   is_from_suggestion: data.isFromSuggestion,
  // })
}

// ── AI Assistant events ──────────────────────────────────────────────────────
// Simple engagement funnel: opened → message/action → tool confirmed. Errors
// tracked separately (cold starts / outages show up here in prod).

export function trackAssistantOpened(data: { trigger: 'auto' | 'manual' | 'launcher'; projectId: string }) {
  pushToDataLayer({
    event: 'assistant_opened',
    trigger: data.trigger,
    project_id: data.projectId,
  })
}

// Prompt text is never sent here — content lives in the backend's own AI logs.
export function trackAssistantMessageSent(data: {
  conversationId: string
  messageIndex: number
  promptLength: number
}) {
  pushToDataLayer({
    event: 'assistant_message_sent',
    conversation_id: data.conversationId,
    message_index: data.messageIndex,
    prompt_length: data.promptLength,
  })
}

export function trackAssistantResponseReceived(data: {
  conversationId: string
  messageIndex: number
  latencyMs: number
  transport: 'stream' | 'buffered'
}) {
  pushToDataLayer({
    event: 'assistant_response_received',
    conversation_id: data.conversationId,
    message_index: data.messageIndex,
    latency_ms: data.latencyMs,
    transport: data.transport,
  })
}

export function trackAssistantActionClicked(data: {
  label: string
  surface: 'next_step_banner' | 'launcher' | 'home_chip' | 'measure_picker' | 'workflow_assist' | 'on_this_sheet'
}) {
  pushToDataLayer({
    event: 'assistant_action_clicked',
    action_label: data.label,
    surface: data.surface,
  })
}

// Where a card came from: the assistant's reply, or the app's own suggestion rules (no AI call).
export type CardOrigin = 'assistant' | 'rule'

/** What Draw it for me's card added, and which parts a scope card inserted. */
export interface ConfirmedDetails {
  shape?: 'line' | 'area' | 'count'
  itemsProposed?: number
  itemsAdded?: number
  marksAdded?: number
  height?: 'cited' | 'typed' | 'set_later' | 'none'
  parts?: string
  replace?: boolean
}

const confirmedParams = (d: ConfirmedDetails) => ({
  ...(d.shape ? { shape: d.shape } : {}),
  ...(d.itemsProposed !== undefined ? { items_proposed: d.itemsProposed } : {}),
  ...(d.itemsAdded !== undefined ? { items_added: d.itemsAdded } : {}),
  ...(d.marksAdded !== undefined ? { marks_added: d.marksAdded } : {}),
  ...(d.height ? { height: d.height } : {}),
  ...(d.parts ? { parts: d.parts } : {}),
  ...(d.replace !== undefined ? { replace: d.replace } : {}),
})

export function trackAssistantToolConfirmed(data: { tool: string; origin?: CardOrigin } & ConfirmedDetails) {
  pushToDataLayer({
    event: 'assistant_tool_confirmed',
    tool: data.tool,
    ...(data.origin ? { origin: data.origin } : {}),
    ...confirmedParams(data),
  })
}

export function trackAssistantToolDismissed(data: { tool: string; origin?: CardOrigin }) {
  pushToDataLayer({
    event: 'assistant_tool_dismissed',
    tool: data.tool,
    ...(data.origin ? { origin: data.origin } : {}),
  })
}

// Draw it for me on an On this sheet row: what it was asked to draw. tag_hits counts the printed-tag hints.
export function trackAssistantDrawRequested(data: {
  shape: 'line' | 'area' | 'count'
  kind: string
  rerun: boolean
  tagHits: number
}) {
  pushToDataLayer({
    event: 'assistant_draw_requested',
    shape: data.shape,
    kind: data.kind,
    rerun: data.rerun,
    tag_hits: data.tagHits,
  })
}

export function trackAssistantDetailsOpened(data: { surface: 'measure_card' | 'runs_card' }) {
  pushToDataLayer({ event: 'assistant_details_opened', surface: data.surface })
}

// A workflow panel folded into its title row in the assistant, or opened back up.
export function trackWorkflowPanelMinimized(data: { panel: string; minimized: boolean }) {
  pushToDataLayer({ event: data.minimized ? 'workflow_panel_minimized' : 'workflow_panel_restored', panel: data.panel })
}

// cut: the proxy dropped a turn that ran too long; it is not rerun.
export function trackAssistantError(data: { kind: 'server' | 'network' | 'cut'; conversationId?: string }) {
  pushToDataLayer({
    event: 'assistant_error',
    kind: data.kind,
    conversation_id: data.conversationId,
  })
}

// Paywall reporting counts only this name.
export function trackProFeatureBlocked(data: { featureName: string }) {
  pushToDataLayer({ event: 'pro_feature_blocked', feature_name: data.featureName })
}

export type UpsellSource = 'billing' | 'pro_upgrade_modal' | 'assistant_paywall' | 'header' | 'sidebar_banner'

// planId keeps the camelCase key the billing page has always sent, so existing reports keep matching.
export function trackUpsellClicked(data: { source: UpsellSource; featureName?: string; planId?: string }) {
  pushToDataLayer({
    event: 'upsell_clicked',
    source: data.source,
    feature_name: data.featureName,
    planId: data.planId,
  })
}

export type AutoTakeFailureReason = 'start_request_failed' | 'job_failed' | 'results_unreadable'

// page_id is the page on screen when it fails; job_id ties the failure to its run.
export function trackAutoTakeFailed(data: { reason: AutoTakeFailureReason; jobId?: string; httpStatus?: number }) {
  pushToDataLayer({
    event: 'auto_take_failed',
    reason: data.reason,
    job_id: data.jobId,
    http_status: data.httpStatus,
  })
}

// Symbol or Text matches put into a count after review; `found - added` were unticked first.
export function trackCountMatchesAdded(data: { source: 'symbol' | 'text'; found: number; added: number }) {
  pushToDataLayer({ event: 'count_matches_added', source: data.source, found: data.found, added: data.added })
}

// An existing comment deleted, from its popover or from the toolbar's comment bar.
export function trackCommentDeleted(data: { commentId: string; source: 'toolbar' | 'comment_popover' }) {
  pushToDataLayer({ event: 'comment_deleted', comment_id: data.commentId, source: data.source })
}

export function trackUploadLimitReached(data: { trial: boolean }) {
  const event = data.trial ? 'trial_upload_limit_reached' : 'free_upload_limit_reached'
  const eventCategory = data.trial ? 'Pro Trial' : 'Free Subscription'
  pushToDataLayer({ event, eventCategory, eventAction: 'Upload Limit Reached' })
  pushIntercomEvent(event, { event_category: eventCategory, event_action: 'Upload Limit Reached' })
}

// ── Drawing set (revision) events ────────────────────────────────────────────
// Funnel: upload modal opened → revision uploaded (or failed) → sets managed
// (activated / viewed / renamed / deleted). Sources distinguish the entry
// surfaces so we learn where revision management actually happens.

export function trackDrawingRevisionUploaded(data: {
  projectId: string
  setType: string
  sheetCount: number
  copyTakeoff: boolean
  carriedShapes: number
}) {
  pushToDataLayer({
    event: 'drawing_revision_uploaded',
    project_id: data.projectId,
    set_type: data.setType,
    sheet_count: data.sheetCount,
    copy_takeoff: data.copyTakeoff,
    carried_shapes: data.carriedShapes,
  })
}

export function trackDrawingRevisionUploadFailed(data: { projectId: string; reason: string }) {
  pushToDataLayer({
    event: 'drawing_revision_upload_failed',
    project_id: data.projectId,
    reason: data.reason,
  })
}

// Keeps the old file browser's shape (project_name included) so existing project_viewed reports carry on.
export function trackProjectViewed(data: { projectId: string; projectName: string }) {
  pushToDataLayer({ event: 'project_viewed', project_id: data.projectId, project_name: data.projectName })
}

export function trackDrawingsOrganizerOpened(data: {
  projectId: string
  source:
    | 'chip'
    | 'logo_menu'
    | 'sidebar_upload'
    | 'sidebar_menu'
    | 'assistant_upload'
    | 'header_tools'
    | 'page_scopes_upload'
}) {
  pushToDataLayer({
    event: 'drawings_organizer_opened',
    project_id: data.projectId,
    source: data.source,
  })
}

export function trackDrawingSetCostCompareOpened(data: { projectId: string; setCount: number }) {
  pushToDataLayer({
    event: 'drawing_set_cost_compare_opened',
    project_id: data.projectId,
    set_count: data.setCount,
  })
}

export function trackDrawingSetActivated(data: {
  projectId: string
  drawingSetId: string
  setType?: string
  source: 'use_for_takeoff' | 'assistant' | 'organizer' | 'organizer_restore'
}) {
  pushToDataLayer({
    event: 'drawing_set_activated',
    project_id: data.projectId,
    drawing_set_id: data.drawingSetId,
    set_type: data.setType,
    source: data.source,
  })
}

export function trackDrawingSetCompared(data: {
  projectId: string
  drawingSetId: string
  setType?: string
  source: 'assistant' | 'organizer' | 'organizer_overlay'
}) {
  pushToDataLayer({
    event: 'drawing_set_compare_opened',
    project_id: data.projectId,
    drawing_set_id: data.drawingSetId,
    set_type: data.setType,
    source: data.source,
  })
}

export function trackDrawingSetRenamed(data: { projectId: string; drawingSetId: string }) {
  pushToDataLayer({
    event: 'drawing_set_renamed',
    project_id: data.projectId,
    drawing_set_id: data.drawingSetId,
  })
}

export function trackDrawingSetDeleted(data: { projectId: string; drawingSetId: string; setType?: string }) {
  pushToDataLayer({
    event: 'drawing_set_deleted',
    project_id: data.projectId,
    drawing_set_id: data.drawingSetId,
    set_type: data.setType,
  })
}

// ── Drawings organizer events ────────────────────────────────────────────────
// Organizing (browse → edit → file into folders) and uploads by intent, so we see how sets get prepared for takeoff.

export function trackDrawingsUploaded(data: {
  projectId: string
  intent: 'add' | 'issue' | 'separate'
  firstUpload: boolean
  fileCount: number
  failedFileCount: number
}) {
  pushToDataLayer({
    event: 'drawings_uploaded',
    project_id: data.projectId,
    intent: data.intent,
    first_upload: data.firstUpload,
    file_count: data.fileCount,
    failed_file_count: data.failedFileCount,
  })
}

export function trackDrawingSetBrowsed(data: {
  projectId: string
  drawingSetId: string
  status: 'working' | 'in_review' | 'available' | 'history'
}) {
  pushToDataLayer({
    event: 'drawing_set_browsed',
    project_id: data.projectId,
    drawing_set_id: data.drawingSetId,
    status: data.status,
  })
}

export function trackDrawingSheetEdited(data: { projectId: string; field: 'title' | 'number' }) {
  pushToDataLayer({ event: 'drawing_sheet_edited', project_id: data.projectId, field: data.field })
}

export function trackDrawingSheetDuplicated(data: { projectId: string; pageId: string; sourcePageId: string }) {
  pushToDataLayer({
    event: 'drawing_sheet_duplicated',
    project_id: data.projectId,
    page_id: data.pageId,
    source_page_id: data.sourcePageId,
  })
}

export function trackProjectMadeAvailableOffline(data: { projectId: string; sheetCount: number; failedCount: number }) {
  pushToDataLayer({
    event: 'project_made_available_offline',
    project_id: data.projectId,
    sheet_count: data.sheetCount,
    failed_count: data.failedCount,
  })
}

export function trackOfflineEditSynced(data: { projectId: string; pageId: string }) {
  pushToDataLayer({ event: 'offline_edit_synced', project_id: data.projectId, page_id: data.pageId })
}

type OrganizeSource = 'organizer' | 'sidebar'

export function trackDrawingSheetStarred(data: {
  projectId: string
  starred: boolean
  count: number
  source: 'organizer' | 'sidebar_select'
}) {
  pushToDataLayer({
    event: 'drawing_sheet_starred',
    project_id: data.projectId,
    starred: data.starred,
    count: data.count,
    source: data.source,
  })
}

export function trackDrawingSheetsMoved(data: {
  projectId: string
  count: number
  method: 'drag' | 'move_to'
  source: OrganizeSource
}) {
  pushToDataLayer({
    event: 'drawing_sheets_moved',
    project_id: data.projectId,
    count: data.count,
    method: data.method,
    source: data.source,
  })
}

export function trackDrawingSheetsDeleted(data: { projectId: string; count: number; source: 'sidebar_select' }) {
  pushToDataLayer({ event: 'drawing_sheets_deleted', project_id: data.projectId, count: data.count, source: data.source })
}

export function trackSheetFilterApplied(data: {
  projectId: string
  filter: 'starred' | 'has_takeoff' | 'has_markups' | 'needs_scale'
  view: 'takeoff' | 'estimate'
}) {
  pushToDataLayer({ event: 'sheet_filter_applied', project_id: data.projectId, filter: data.filter, view: data.view })
}

export type TakeoffExportSource = 'export_button' | 'select_sheets' | 'row_checkbox'

export function trackTakeoffExportOpened(data: { projectId: string; pageCount: number; source: TakeoffExportSource }) {
  pushToDataLayer({
    event: 'takeoff_export_opened',
    project_id: data.projectId,
    page_count: data.pageCount,
    source: data.source,
  })
}

export type EstimateCsvSource = 'estimate_header' | 'estimate_sidebar' | 'mobile_bar' | 'assistant'

export function trackEstimateCsvExported(data: { projectId: string; sectionName: string; source: EstimateCsvSource }) {
  pushToDataLayer({
    event: 'exported_takeoff_summary_csv',
    project_id: data.projectId,
    section_name: data.sectionName,
    source: data.source,
  })
}

export function trackEstimateNarrowed(data: { projectId: string; by: 'sheet' | 'material' }) {
  pushToDataLayer({ event: 'estimate_narrowed', project_id: data.projectId, by: data.by })
}

export type EstimateClearSource = 'clear' | 'close'

export function trackEstimateNarrowingCleared(data: { projectId: string; source: EstimateClearSource }) {
  pushToDataLayer({ event: 'estimate_narrowing_cleared', project_id: data.projectId, source: data.source })
}

export function trackCustomPdfDownloaded(data: {
  projectId: string
  projectName: string
  pageCount: number
  materialCount: number
  measurementCount: number
  source: 'sidebar_export'
}) {
  pushToDataLayer({
    event: 'custom_pdf_downloaded',
    project_name: data.projectName,
    project_id: data.projectId,
    page_count: data.pageCount,
    material_count: data.materialCount,
    measurement_count: data.measurementCount,
    source: data.source,
  })
}

export function trackQuantitiesCsvExported(data: {
  projectId: string
  pageCount: number
  materialCount: number
  measurementCount: number
  source: 'sidebar_export'
}) {
  pushToDataLayer({
    event: 'exported_takeoff_summary_csv',
    project_id: data.projectId,
    page_count: data.pageCount,
    material_count: data.materialCount,
    measurement_count: data.measurementCount,
    source: data.source,
  })
}

export function trackDrawingSheetHistoryOpened(data: { projectId: string }) {
  pushToDataLayer({ event: 'drawing_sheet_history_opened', project_id: data.projectId })
}

export function trackDrawingFolderCreated(data: { projectId: string; source: OrganizeSource }) {
  pushToDataLayer({ event: 'drawing_folder_created', project_id: data.projectId, source: data.source })
}

export function trackDrawingFolderDeleted(data: { projectId: string; source: OrganizeSource }) {
  pushToDataLayer({ event: 'drawing_folder_deleted', project_id: data.projectId, source: data.source })
}

export function trackExternalDetailLinked(data: {
  projectId: string
  discipline: string
  source: 'suggested' | 'browse'
  replaced: boolean
}) {
  pushToDataLayer({
    event: 'external_detail_linked',
    project_id: data.projectId,
    discipline: data.discipline,
    source: data.source,
    replaced: data.replaced,
  })
}

export function trackExternalDetailUnlinked(data: { projectId: string; discipline: string }) {
  pushToDataLayer({ event: 'external_detail_unlinked', project_id: data.projectId, discipline: data.discipline })
}
